import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Filter, FileText, X } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import SortSelect, { applySort, cmpStr, cmpDate, cmpBool } from "@/components/SortSelect";
import { fetchProducts, fetchCatalogStatus, fetchSettings, uploadUrl } from "@/lib/api";
import { variantName } from "@/lib/variants";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["Tops", "Bottoms", "Dresses", "Outerwear", "Accessories"];

const SHOP_SORT_OPTIONS = [
  { value: "name_asc", label: "Name A → Z" },
  { value: "name_desc", label: "Name Z → A" },
  { value: "newest", label: "Newest first" },
  { value: "featured", label: "Featured first" },
];

const SHOP_SORT_COMPARATORS = {
  name_asc: cmpStr((p) => p.name, "asc"),
  name_desc: cmpStr((p) => p.name, "desc"),
  newest: cmpDate((p) => p.created_at, "desc"),
  featured: cmpBool((p) => p.featured, true),
};

// Catalog PDFs come from admin uploads (served from /uploads/catalogs/)
// API: GET /api/admin/uploads/catalogs/status returns { prebook: {url} | null, in_stock: {url} | null }

export default function Shop() {
  const { isApproved } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Prebook is gated to approved buyers only. Anyone else lands on In Stock.
  const rawType = searchParams.get("type") === "in_stock" ? "in_stock" : "prebook";
  const urlType = !isApproved && rawType === "prebook" ? "in_stock" : rawType;
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeCats, setActiveCats] = useState([]);
  const [activeSizes, setActiveSizes] = useState([]);
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [activeType, setActiveType] = useState(urlType); // "prebook" | "in_stock"
  const [catalogs, setCatalogs] = useState({}); // { prebook: {url}|null, in_stock: {url}|null }
  const [prebookLabel, setPrebookLabel] = useState("Prebook · SS26");
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [categories, setCategories] = useState(CATEGORIES);
  const [sort, setSort] = useState("name_asc");

  // Keep state in sync with URL (Quick Actions / back-forward navigation)
  useEffect(() => {
    setActiveType(urlType);
  }, [urlType]);

  const switchType = (t) => {
    setActiveType(t);
    setSearchParams((sp) => {
      sp.set("type", t);
      return sp;
    }, { replace: true });
  };

  useEffect(() => {
    (async () => {
      try {
        const [prods, status, settings] = await Promise.all([
          fetchProducts(),
          fetchCatalogStatus().catch(() => ({})),
          fetchSettings().catch(() => ({})),
        ]);
        setProducts(prods);
        setCatalogs(status || {});
        if (settings?.prebook_label) setPrebookLabel(settings.prebook_label);
        // Show every category actually used by a product, plus any admin-
        // configured categories the store still wants promoted. Preserves the
        // configured order and appends unused-but-present product categories.
        const configured = settings?.categories?.length ? settings.categories : CATEGORIES;
        const inUse = Array.from(new Set(prods.map((p) => p.category).filter(Boolean)));
        const merged = [...configured, ...inUse.filter((c) => !configured.includes(c))];
        setCategories(merged);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const allSizes = useMemo(() => {
    const set = new Set();
    products.forEach((p) => (p.sizes || []).forEach((s) => {
      const n = variantName(s);
      if (n) set.add(n);
    }));
    return Array.from(set);
  }, [products]);

  const toggle = (list, setList, val) =>
    setList(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const result = products.filter((p) => {
      if ((p.product_type || "prebook") !== activeType) return false;
      if (showFeaturedOnly && !p.featured) return false;
      if (q && !`${p.name} ${p.description}`.toLowerCase().includes(q)) return false;
      if (activeCats.length && !activeCats.includes(p.category)) return false;
      if (activeSizes.length && !(p.sizes || []).some((s) => activeSizes.includes(variantName(s)))) return false;
      return true;
    });
    return applySort(result, sort, SHOP_SORT_COMPARATORS);
  }, [products, query, activeCats, activeSizes, showFeaturedOnly, activeType, sort]);

  const counts = useMemo(() => {
    const c = { prebook: 0, in_stock: 0 };
    products.forEach((p) => { c[p.product_type || "prebook"] = (c[p.product_type || "prebook"] || 0) + 1; });
    return c;
  }, [products]);

  // Pull the first prebook product's ship window / order-by cutoff so the drop
  // header can headline them. If different products have different windows we
  // just show the first one — a mixed-window drop is unusual and the buyer sees
  // the accurate per-product dates on the card.
  const dropHeadline = useMemo(() => {
    const first = products.find((p) => (p.product_type || "prebook") === "prebook" && (p.delivery_window || p.order_by_date));
    return {
      window: first?.delivery_window || "",
      orderBy: first?.order_by_date || "",
    };
  }, [products]);

  const clearFilters = () => { setActiveCats([]); setActiveSizes([]); setShowFeaturedOnly(false); setQuery(""); };
  const activeFilterCount = activeCats.length + activeSizes.length + (showFeaturedOnly ? 1 : 0) + (query ? 1 : 0);

  return (
    <div data-testid="shop-page">
      <div className="max-w-[1400px] mx-auto flex page-card">
        <aside className="hidden md:block w-[var(--sidebar-w)] shrink-0 border-r border-border min-h-[calc(100vh-var(--topbar-h))] sticky top-[var(--topbar-h)] self-start" data-testid="shop-sidebar">
          <div className="p-5 space-y-6">
            <FilterPanel
              query={query} setQuery={setQuery}
              categories={categories} activeCats={activeCats} setActiveCats={setActiveCats}
              allSizes={allSizes} activeSizes={activeSizes} setActiveSizes={setActiveSizes}
              showFeaturedOnly={showFeaturedOnly} setShowFeaturedOnly={setShowFeaturedOnly}
              activeFilterCount={activeFilterCount} clearFilters={clearFilters}
              toggle={toggle}
            />
          </div>
        </aside>

        {/* Mobile filter drawer */}
        {mobileFiltersOpen && (
          <div
            className="md:hidden fixed inset-0 z-50 flex"
            data-testid="shop-mobile-filters"
          >
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setMobileFiltersOpen(false)}
              aria-hidden="true"
            />
            <div className="relative bg-white w-[85%] max-w-sm h-full overflow-y-auto shadow-xl ml-0 animate-in slide-in-from-left">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border sticky top-0 bg-white">
                <p className="text-xs uppercase tracking-[0.18em] font-medium">Filters</p>
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="p-2 -m-2 text-muted-foreground hover:text-foreground"
                  aria-label="Close filters"
                >
                  <X size={18} strokeWidth={1.75} />
                </button>
              </div>
              <div className="p-5 space-y-6">
                <FilterPanel
                  query={query} setQuery={setQuery}
                  categories={categories} activeCats={activeCats} setActiveCats={setActiveCats}
                  allSizes={allSizes} activeSizes={activeSizes} setActiveSizes={setActiveSizes}
                  showFeaturedOnly={showFeaturedOnly} setShowFeaturedOnly={setShowFeaturedOnly}
                  activeFilterCount={activeFilterCount} clearFilters={clearFilters}
                  toggle={toggle}
                />
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(false)}
                  className="w-full py-3 rounded bg-primary text-primary-foreground text-sm font-medium"
                >
                  Show {filtered.length} {filtered.length === 1 ? "style" : "styles"}
                </button>
              </div>
            </div>
          </div>
        )}

        <section className="flex-1 min-w-0">
          <div className="px-4 md:px-6 py-5 border-b border-border bg-white sticky top-[var(--topbar-h)] z-10">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h1 className="text-2xl font-semibold">
                  {activeType === "prebook" ? prebookLabel : "In Stock Now"}
                </h1>
                {activeType === "prebook" && (
                  <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mt-1">Minimum order: 20 units</p>
                )}
                {activeType === "prebook" && (dropHeadline.window || dropHeadline.orderBy) && (
                  <div className="mt-2 leading-tight">
                    {dropHeadline.window && (
                      <p className="font-display text-xl md:text-2xl text-destructive">
                        Estimated ship window · {dropHeadline.window}
                      </p>
                    )}
                    {dropHeadline.orderBy && (
                      <p className="font-display text-xl md:text-2xl text-destructive">
                        Order by {dropHeadline.orderBy}
                      </p>
                    )}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4 text-sm">
                {isApproved && catalogs[activeType]?.url ? (
                  <a
                    href={uploadUrl(catalogs[activeType].url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`shop-pdf-${activeType}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-foreground/20 rounded-full text-foreground hover:bg-foreground hover:text-background transition-colors text-[12px] tracking-[0.05em] uppercase"
                  >
                    <FileText size={13} strokeWidth={1.5} />
                    View lookbook PDF
                  </a>
                ) : null}
                <SortSelect
                  value={sort}
                  onChange={setSort}
                  options={SHOP_SORT_OPTIONS}
                  testid="shop-sort"
                />
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  data-testid="shop-mobile-filters-trigger"
                  className="md:hidden inline-flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-full text-foreground text-[12px] tracking-[0.05em] uppercase"
                >
                  <Filter size={13} strokeWidth={1.5} />
                  Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
                </button>
                <div className="hidden md:flex items-center gap-2 text-muted-foreground tabular-nums" data-testid="shop-results-count">
                  <Filter size={13} strokeWidth={1.5} />
                  {filtered.length} of {counts[activeType] || 0} styles
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-1 border-b border-transparent -mb-5 pb-0">
              {isApproved && (
                <TypeTab
                  active={activeType === "prebook"}
                  onClick={() => switchType("prebook")}
                  testid="shop-tab-prebook"
                >
                  {prebookLabel} <span className="ml-1.5 text-xs opacity-60">{counts.prebook || 0}</span>
                </TypeTab>
              )}
              <TypeTab
                active={activeType === "in_stock"}
                onClick={() => switchType("in_stock")}
                testid="shop-tab-in-stock"
              >
                In Stock Now <span className="ml-1.5 text-xs opacity-60">{counts.in_stock || 0}</span>
              </TypeTab>
            </div>
          </div>

          <div className="px-4 md:px-6 py-6">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="shop-loading">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="aspect-[4/5] rounded-md bg-secondary animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-24 border border-dashed border-border rounded-md" data-testid="shop-empty">
                <p className="text-base font-medium mb-1">No matches</p>
                <p className="text-sm text-muted-foreground">Try adjusting filters or clearing them all.</p>
                {activeFilterCount > 0 && (
                  <button onClick={clearFilters} className="mt-4 text-sm text-primary hover:underline">Clear filters</button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-testid="shop-grid">
                {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function FilterPanel({ query, setQuery, categories, activeCats, setActiveCats, allSizes, activeSizes, setActiveSizes, showFeaturedOnly, setShowFeaturedOnly, activeFilterCount, clearFilters, toggle }) {
  return (
    <>
      <div>
        <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-2">Search</p>
        <div className="relative">
          <Search size={13} strokeWidth={1.5} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="shop-search"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search styles…"
            className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded focus:border-primary"
          />
        </div>
      </div>

      <FilterGroup title="Category">
        {categories.map((c) => (
          <FilterChip
            key={c}
            testid={`filter-cat-${c.toLowerCase()}`}
            active={activeCats.includes(c)}
            onClick={() => toggle(activeCats, setActiveCats, c)}
          >
            {c}
          </FilterChip>
        ))}
      </FilterGroup>

      {allSizes.length > 0 && (
        <FilterGroup title="Size">
          {allSizes.map((s) => (
            <FilterChip
              key={s}
              testid={`filter-size-${s}`}
              active={activeSizes.includes(s)}
              onClick={() => toggle(activeSizes, setActiveSizes, s)}
            >
              {s}
            </FilterChip>
          ))}
        </FilterGroup>
      )}

      <div>
        <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-2">Curated</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            data-testid="filter-featured"
            type="checkbox"
            checked={showFeaturedOnly}
            onChange={(e) => setShowFeaturedOnly(e.target.checked)}
            className="accent-[hsl(var(--primary))]"
          />
          Featured only
        </label>
      </div>

      {activeFilterCount > 0 && (
        <button
          onClick={clearFilters}
          data-testid="filter-clear"
          className="text-xs uppercase tracking-[0.12em] text-primary hover:underline"
        >
          Clear filters ({activeFilterCount})
        </button>
      )}
    </>
  );
}

function FilterGroup({ title, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mb-2">{title}</p>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function FilterChip({ active, onClick, testid, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-2.5 py-1 text-xs rounded border transition-colors ${
        active ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-foreground/80 hover:border-foreground/40"
      }`}
    >
      {children}
    </button>
  );
}

function TypeTab({ active, onClick, testid, children }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-4 py-2.5 text-sm font-medium tracking-wide border-b-2 transition-colors ${
        active
          ? "border-foreground text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}
